/** @format */
import angular from 'angular';
import _ from 'lodash';
import { localeServiceModule } from 'sis-common';
import { errorServiceModule } from '../error-handler/legacy/errorService';
import { PaginationComponent } from '../pagination/pagination.component';
import { searchAndResultsModule } from '../search/searchAndResults.dir';
import { searchFilterOptionsMenuModule } from '../search/searchFilterOptionsMenu.component';
import { searchFilterModule } from '../search/searchFilters/searchFilter.component';
import { searchParameterServiceModule } from '../search/searchParameters';
import { searchParametersDirectiveModule } from '../search/searchParameters.dir';
import { commonCourseUnitServiceModule } from '../service/courseUnit.service';
import { commonModuleServiceModule } from '../service/module.service';
import { plainTextFilterModule } from '../string/plain-text.filter';
import { messageModule } from './message.component';
import messagesTpl from './messages.tpl.html';

export const messagesModule = 'sis-components.messenger.messages';

(function () {
    messagesController.$inject = [
        '$scope',
        '$state',
        '$injector',
        'commonMessageService',
        'commonModuleService',
        'commonCourseUnitService',
        'AuthService',
        'SearchParameters',
        'SearchFilterTypes',
        'MessageType',
        'defaultPromiseHandler',
        'messageJSDataModel',
        'localeService',
    ];
    angular
        .module(messagesModule, [
            searchFilterModule,
            searchAndResultsModule,
            searchParametersDirectiveModule,
            searchFilterOptionsMenuModule,
            searchParameterServiceModule,
            localeServiceModule,
            commonModuleServiceModule,
            commonCourseUnitServiceModule,
            'sis-components.date.localDateFormatFilter',
            messageModule,
            'sis-components.service.commonMessageService',
            errorServiceModule,
            plainTextFilterModule,
            'sis-components.model.message',
            PaginationComponent.downgrade.moduleName,
        ])
        .component('messages', {
            template: messagesTpl,
            controller: messagesController,
            bindings: {
                messageSearchMethod: '<',
                messageFilterType: '<',
                messageDestinationRoute: '<',
                confinedContainer: '<',
                removeSenderStudentNumber: '<',
            },
        });

    function messagesController(
        $scope,
        $state,
        $injector, // NOSONAR
        commonMessageService,
        commonModuleService,
        commonCourseUnitService,
        AuthService,
        SearchParameters,
        SearchFilterTypes,
        MessageType,
        defaultPromiseHandler,
        messageJSDataModel,
        localeService,
    ) {
        const $ctrl = this;
        $ctrl.messagesPerPage = 10;
        $ctrl.maxSize = 5;
        $ctrl.snippetLength = _.get($state, 'current.data.messageSnippetLength', 100);
        $ctrl.showMessagesHeading = !!_.get($state, 'current.data.showMessagesHeading');
        $ctrl.showMessageFilters = !!_.get($state, 'current.data.showMessageFilters');
        $ctrl.currentState = $state.current?.name;

        $ctrl.$onInit = () => {
            const customMethod = _.get($ctrl, 'messageSearchMethod');
            $ctrl.messageFilterType = _.get($ctrl, 'messageFilterType');
            $ctrl.messageSearchMethod = _.isFunction(customMethod) ? customMethod : commonMessageService.findAllReceivedMessages;
            $ctrl.currentPage = 1;

            $ctrl.searchParams = new SearchParameters({}, { allowEmptySearchString: true });
            $ctrl.messageTargetNamesById = new Map();

            const messageTypeOptions = _.map(_.keys(MessageType), messageTypeKey => ({
                name: `SIS_COMPONENTS.MESSENGER.MESSAGE.TYPE.${_.get(MessageType, messageTypeKey)}.NAME`,
                id: messageTypeKey,
            }));

            $ctrl.searchParameterOptions = {
                searchString: {
                    name: 'SEARCHSTRING',
                    type: SearchFilterTypes.NO_POPOVER,
                    hide: true,
                },
                messageType: {
                    name: 'MESSAGETYPE',
                    options: messageTypeOptions,
                    type: SearchFilterTypes.MULTI,
                },
            };
            $ctrl.loadMessages($ctrl.removeSenderStudentNumber);
        };

        $ctrl.loadMessages = removeSenderStudentNumber => {
            const start = ($ctrl.currentPage - 1) * $ctrl.messagesPerPage;

            const searchMessageTypes = _.map(_.get($ctrl.searchParams, 'messageType.value'), 'id');
            const searchString = _.get($ctrl.searchParams, 'searchString.value');

            return $ctrl
                .messageSearchMethod(
                    AuthService.personId(),
                    null,
                    start,
                    $ctrl.messagesPerPage,
                    $ctrl.messageFilterType,
                    _.get(searchString, 'length') > 0 ? searchString : null,
                    searchMessageTypes,
                )
                .then(result => {
                    $ctrl.totalItems = result.total;
                    $ctrl.messages = removeSenderStudentNumber
                        ? result.messages.map(message => ({ ...message, senderStudentNumber: null }))
                        : result.messages;
                    $ctrl.loadRelationsForCourseUnitRealisationMessages($ctrl.messages);
                    $ctrl.resolveMessageTargetNames($ctrl.messages);
                })
                .catch(defaultPromiseHandler.loggingRejectedPromiseHandler);
        };

        $ctrl.onChange = () => $ctrl.loadMessages($ctrl.removeSenderStudentNumber);

        $ctrl.getSnippet = message => {
            const comment = _.get(message, 'comment', '') || '';
            return comment.substring(0, $ctrl.snippetLength).concat('...');
        };

        $ctrl.getLocalizedSnippet = message => {
            const comment = localeService.getLocalizedValue(_.get(message, 'localizedComment', '')) || '';
            return comment.substring(0, $ctrl.snippetLength).concat('...');
        };

        $ctrl.getMessageTypeTranslationKey = message => commonMessageService.getMessageTypeTranslationKey(message);

        $ctrl.getMessageTargetName = message => $ctrl.messageTargetNamesById.get(message.id);

        $ctrl.resolveMessageTargetNames = allMessages => _.forEach(allMessages, message => $ctrl.resolveMessageTargetName(message));

        $ctrl.setMessageTargetName = (id, targetName) => {
            $ctrl.messageTargetNamesById.set(id, targetName);
        };

        $ctrl.resolveMessageTargetName = message => {
            if (message.moduleId) {
                commonModuleService
                    .findById(message.moduleId)
                    .then(module => localeService.getLocalizedValue(module.name))
                    .then(targetName => $ctrl.setMessageTargetName(message.id, targetName));
            }
            if (message.courseUnitId) {
                commonCourseUnitService
                    .findById(message.courseUnitId)
                    .then(courseUnit => localeService.getLocalizedValue(courseUnit.name))
                    .then(targetName => $ctrl.setMessageTargetName(message.id, targetName));
            }
            if (message.customStudyDraft) {
                $ctrl.setMessageTargetName(message.id, message.customStudyDraft.name);
            }
        };

        $ctrl.searchService = {
            search() {
                return $ctrl.loadMessages($ctrl.removeSenderStudentNumber);
            },
        };

        $ctrl.hasValidSearchParameters = () =>
            $ctrl.searchParams.searchString.isValidForSearch() || $ctrl.searchParams.messageType.isValidForSearch();

        $ctrl.loadRelationsForCourseUnitRealisationMessages = allMessages => {
            _.forEach(_.filter(allMessages, filterCourseUnitRealisationMessages), message =>
                messageJSDataModel.loadRelations(message, ['courseUnitRealisation']),
            );
        };

        $ctrl.loadRelationsForCourseUnitMessages = allMessages => {
            _.forEach(
                _.filter(allMessages, message => _.has(message, 'courseUnitId')),
                message => messageJSDataModel.loadRelations(message, ['courseUnit']),
            );
        };

        $ctrl.showSenderFullName = message =>
            !message.isAutomaticCourseUnitRealisationMessage && !message.isCourseUnitManualEvaluationRequiredMessage;

        function filterCourseUnitRealisationMessages(message) {
            return message.isManualCourseUnitRealisationMessage || message.isAutomaticCourseUnitRealisationMessage;
        }
    }
})();
